import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import api from '../services/api';

export interface AgingProcessOption {
  id: number;
  batch_id?: number;
  batchId?: number;
  previousFermentationId?: number;
  start_date?: string;
  startDate?: string;
  end_date?: string;
  endDate?: string;
  status: string;
  batch?: {
    id: number;
    batch_id?: string;
    batch_name?: string;
    batchId?: string;
    batchName?: string;
    wine_type?: string;
    wineType?: string;
    vineyard?: { id: number; name: string };
  };
  vessel?: {
    id: number;
    name?: string;
    vesselId?: string;
    vessel_id?: string;
    type?: string;
  };
}

export interface AgingProcessSelectorProps {
  name: string;
  value?: number;
  onChange: (agingId: number, agingData: AgingProcessOption | null) => void;
  label?: string;
  required?: boolean;
  filterStatus?: string[];
  disabled?: boolean;
  error?: string;
  className?: string;
  /** Bump to refetch aging options (e.g. after completing an Active process). */
  refreshKey?: number;
}

const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return 'N/A';
  try {
    const cleanDateStr = dateString.includes('T') ? dateString.split('T')[0] : dateString;
    const date = new Date(cleanDateStr);
    if (isNaN(date.getTime())) return 'Invalid';
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return 'Invalid';
  }
};

const getDurationDays = (start: string | undefined, end: string | undefined): number | null => {
  if (!start || !end) return null;
  try {
    const s = new Date(start.includes('T') ? start.split('T')[0] : start);
    const e = new Date(end.includes('T') ? end.split('T')[0] : end);
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
    return Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

/**
 * Searchable dropdown for selecting an aging process for bottling.
 * Default filter is Completed; callers may include Active as well.
 */
const AgingProcessSelector: React.FC<AgingProcessSelectorProps> = ({
  name,
  value,
  onChange,
  label = 'Aging Process',
  required = false,
  filterStatus = ['Completed'],
  disabled = false,
  error,
  className = '',
  refreshKey = 0,
}) => {
  const [agingProcesses, setAgingProcesses] = useState<AgingProcessOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filterStatusKey = filterStatus.join(',');
  const completedOnly =
    filterStatus.length === 1 && filterStatus[0] === 'Completed';

  useEffect(() => {
    const fetchAging = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/aging', {
          params: { include: 'batch.vineyard,vessel' },
        });
        const data = Array.isArray(response.data) ? response.data : [];
        const statusList = filterStatusKey ? filterStatusKey.split(',') : [];
        const filtered = data.filter((a: AgingProcessOption) =>
          statusList.length === 0 ? true : statusList.includes(a.status)
        );
        setAgingProcesses(filtered);
      } catch (err) {
        console.error('Error fetching aging processes:', err);
        setAgingProcesses([]);
      } finally {
        setLoading(false);
      }
    };
    fetchAging();
  }, [filterStatusKey, refreshKey]);

  const filteredOptions = agingProcesses.filter((aging) => {
    const batchName =
      aging.batch?.batch_name ?? aging.batch?.batchName ?? '';
    const vesselName = aging.vessel?.name ?? aging.vessel?.type ?? '';
    const search = searchQuery.toLowerCase();
    return (
      batchName.toLowerCase().includes(search) ||
      vesselName.toLowerCase().includes(search) ||
      String(aging.id).includes(search) ||
      String(aging.status).toLowerCase().includes(search)
    );
  });

  const selectedAging = agingProcesses.find((a) => a.id === value);

  const getOptionLabel = (aging: AgingProcessOption) => {
    const batchName =
      aging.batch?.batch_name ?? aging.batch?.batchName ?? `Aging #${aging.id}`;
    const vesselName =
      aging.vessel?.name ?? aging.vessel?.type ?? 'Unknown vessel';
    const start = aging.start_date ?? aging.startDate;
    const end = aging.end_date ?? aging.endDate;
    const days = getDurationDays(start, end);
    const duration = days != null ? ` (${days}d)` : '';
    const status = aging.status ? ` | ${aging.status}` : '';
    return `${batchName} | ${vesselName} | ${formatDate(start)} – ${formatDate(end)}${duration}${status}`;
  };

  const handleSelect = (aging: AgingProcessOption) => {
    onChange(aging.id, aging);
    setSearchQuery('');
    setIsOpen(false);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(0, null);
    setSearchQuery('');
    setIsOpen(false);
  };

  const fieldId = `aging-select-${name}`;
  const hasError = !!error;

  return (
    <div className={className}>
      {label && (
        <label
          htmlFor={fieldId}
          className={`block text-sm font-normal mb-2 ${hasError ? 'text-error-500' : 'text-monday-text-muted'
            }`}
        >
          {label}
          {required && <span className="form-required-dot" />}
        </label>
      )}

      <div className="relative">
        <div
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${fieldId}-listbox`}
          id={fieldId}
          onClick={() => !disabled && setIsOpen(!isOpen)}
          className={`
            mobile-form-input w-full min-h-[40px] flex items-center justify-between pr-10 cursor-pointer
            ${hasError ? 'border-error-500' : 'border-monday-border'}
            ${disabled ? 'bg-monday-header cursor-not-allowed opacity-60' : 'bg-white'}
          `}
        >
          <span className="truncate text-monday-text">
            {loading
              ? 'Loading...'
              : selectedAging
                ? getOptionLabel(selectedAging)
                : 'Select aging process'}
          </span>
          <span className="flex items-center gap-1 flex-shrink-0">
            {value && !disabled && (
              <button
                type="button"
                onClick={handleClear}
                className="p-1 rounded hover:bg-monday-header text-monday-text-muted"
                aria-label="Clear selection"
              >
                {FiIcons.FiX({ size: 16 })}
              </button>
            )}
            {FiIcons.FiChevronDown({
              size: 18,
              className: `text-monday-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`,
            })}
          </span>
        </div>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-10"
              aria-hidden="true"
              onClick={() => setIsOpen(false)}
            />
            <div
              id={`${fieldId}-listbox`}
              role="listbox"
              className="absolute z-20 mt-1 w-full bg-white border border-monday-border rounded-lg shadow-lg max-h-60 overflow-hidden"
            >
              <div className="p-2 border-b border-monday-border-light">
                <input
                  type="text"
                  placeholder="Search batch or vessel..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  className="w-full px-3 py-2 text-sm border border-monday-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  autoFocus
                />
              </div>
              <div className="overflow-y-auto max-h-48">
                {loading ? (
                  <div className="px-4 py-3 text-sm text-monday-text-muted">
                    Loading...
                  </div>
                ) : filteredOptions.length === 0 ? (
                  <div
                    className="px-4 py-3 text-sm text-monday-text-muted space-y-1"
                    data-testid="aging-selector-empty"
                  >
                    {completedOnly ? (
                      <>
                        <p>No completed aging processes found.</p>
                        <p>
                          Complete an aging process first, then return here to bottle.{' '}
                          <Link
                            to="/aging"
                            className="font-medium text-primary-600 underline hover:text-primary-700"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Go to Aging
                          </Link>
                        </p>
                      </>
                    ) : (
                      <>
                        <p>No aging processes available.</p>
                        <p>
                          Start an aging process first, then return here to bottle.{' '}
                          <Link
                            to="/aging"
                            className="font-medium text-primary-600 underline hover:text-primary-700"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Go to Aging
                          </Link>
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  filteredOptions.map((aging) => (
                    <button
                      key={aging.id}
                      type="button"
                      role="option"
                      aria-selected={aging.id === value}
                      onClick={() => handleSelect(aging)}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-monday-header transition-colors ${aging.id === value ? 'bg-primary-50 text-primary-700' : 'text-monday-text'
                        }`}
                    >
                      {getOptionLabel(aging)}
                    </button>
                  ))
                )}
              </div>
            </div>
          </>
        )}
      </div>

      {error && <p className="mt-1 text-xs text-error-500">{error}</p>}
    </div>
  );
};

export default AgingProcessSelector;
